import React, { useState } from 'react';
import { Popper, Paper, MenuList, MenuItem, ListItemIcon, ListItemText, Grow, ClickAwayListener, Divider } from '@mui/material';
import { Logout, Person, Receipt, CalendarMonth, DirectionsCar } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { logger } from '../../utils/logger';
import { useAuth } from '../../hooks/useAuth';

const Header: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [closeTimeout, setCloseTimeout] = useState<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const open = Boolean(anchorEl);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    // Clear any existing timeout
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    // Set a timeout before closing
    const timeout = setTimeout(() => {
      setAnchorEl(null);
      setCloseTimeout(null);
    }, 500);
    setCloseTimeout(timeout);
  };

  const handleMenuMouseEnter = () => {
    // Clear the timeout when mouse enters the menu
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
  };

  const handleMenuMouseLeave = () => {
    // Close immediately when leaving the menu
    if (closeTimeout) {
      clearTimeout(closeTimeout);
    }
    setAnchorEl(null);
    setCloseTimeout(null);
  };

  const handleClickAway = () => {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
    }
    setAnchorEl(null);
    setCloseTimeout(null);
  };

  const handleDashboard = () => {
    // Role-aware navigation
    if (user?.role === 'admin') {
      navigate('/customers');
    } else {
      navigate('/customer/dashboard');
    }
  };

  const handleInvoices = () => {
    logger.info('Navigating to invoices');
    navigate('/admin/invoices');
    handleMouseLeave();
  };

  const handleCalendar = () => {
    logger.info('Navigating to calendar');
    navigate('/admin/calendar');
    handleMouseLeave();
  };

  const handleTrucks = () => {
    logger.info('Navigating to trucks');
    navigate('/admin/trucks');
    handleMouseLeave();
  };

  const handleLogout = async () => {
    logger.info('User logout initiated');
    handleMouseLeave();
    try {
      await logout();
      window.location.href = '/login';
    } catch (error) {
      logger.error('Logout error', { error });
      window.location.href = '/login';
    }
  };

  const displayName = user?.name || user?.email || 'Guest';

  return (
    <header className="text-on-primary shadow-md" style={{ background: 'linear-gradient(to right, #3f8eb7 0%, #296284 100%)' }}>
      <div className="container mx-auto px-3 sm:px-4 py-2.5 sm:py-4 flex justify-between items-center">
        {/* Logo */}
        <div className="flex items-center flex-shrink min-w-0">
          <img
            src="/images/Allens-Carb-OBD-Testing.png"
            alt="Allen's Carb OBD Testing"
            className="h-10 sm:h-12 md:h-14 w-auto object-contain cursor-pointer"
            style={{ maxWidth: '250px' }}
            onClick={handleDashboard}
          />
        </div>

        {/* Navigation and User Info */}
        <div className="flex items-center gap-2 sm:gap-4 md:gap-6 flex-shrink-0">
          {/* User Name - Mobile: icon above name, Desktop: icon next to name */}
          <div className="flex text-white font-medium">
            {/* Mobile: column layout */}
            <div className="flex flex-col items-center md:hidden">
              <Person sx={{ fontSize: 20, mb: 0.5 }} />
              <span className="text-xs">{displayName}</span>
            </div>
            {/* Desktop: row layout */}
            <div className="hidden md:flex items-center">
              <Person sx={{ fontSize: 20, mr: 1 }} />
              {displayName}
            </div>
          </div>

          {/* Dashboard Link with Logout Dropdown */}
          <div
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              id="dashboard-button"
              onClick={handleDashboard}
              className="text-white font-semibold px-3 sm:px-4 py-2 sm:py-2 rounded hover:bg-white/10 transition-colors cursor-pointer text-sm sm:text-base"
              style={{ minHeight: '44px', minWidth: '44px' }}
              aria-label="Dashboard"
              aria-controls={open ? 'logout-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              Dashboard
            </button>

            {/* Logout Dropdown Menu */}
            <Popper
              id="logout-menu"
              open={open}
              anchorEl={anchorEl}
              placement="bottom-end"
              transition
              disablePortal={false}
              modifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [0, 4],
                  },
                },
              ]}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps} timeout={200}>
                  <Paper
                    elevation={8}
                    sx={{
                      minWidth: 150,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    }}
                    onMouseEnter={handleMenuMouseEnter}
                    onMouseLeave={handleMenuMouseLeave}
                  >
                    <ClickAwayListener onClickAway={handleClickAway}>
                      <MenuList
                        autoFocusItem={false}
                        aria-labelledby="dashboard-button"
                      >
                        {/* Admin-only Links */}
                        {user?.role === 'admin' ? [
                          <MenuItem key="calendar" onClick={handleCalendar}>
                            <ListItemIcon>
                              <CalendarMonth fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Calendar</ListItemText>
                          </MenuItem>,
                          <MenuItem key="invoices" onClick={handleInvoices}>
                            <ListItemIcon>
                              <Receipt fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Invoices</ListItemText>
                          </MenuItem>,
                          <MenuItem key="trucks" onClick={handleTrucks}>
                            <ListItemIcon>
                              <DirectionsCar fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Trucks</ListItemText>
                          </MenuItem>,
                          <Divider key="divider" />
                        ] : null}

                        <MenuItem onClick={handleLogout}>
                          <ListItemIcon>
                            <Logout fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Logout</ListItemText>
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
